import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Settings } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "./useAuth";

interface MergedSetting {
  key: string;
  value: unknown;
  hasOverride: boolean;
  defaultValue: unknown;
}

interface MergedSettingsResponse {
  settings: MergedSetting[];
  hasDefaults: boolean;
}

export function useSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: globalSettings = [], isLoading: isLoadingGlobal } = useQuery<Settings[]>({
    queryKey: ['/api/settings'],
    staleTime: 5 * 60 * 1000, // 5 minutes - settings rarely change
  });

  const { data: mergedData, isLoading: isLoadingMerged } = useQuery<MergedSettingsResponse>({
    queryKey: ['/api/settings/merged'],
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes - settings rarely change
  });

  const settings = user && mergedData ? mergedData.settings : globalSettings.map(s => ({
    key: s.key,
    value: s.value,
    hasOverride: false,
    defaultValue: s.value,
  }));

  const updateUserSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      return apiRequest('PUT', `/api/user-settings/${encodeURIComponent(key)}`, { value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/settings/merged'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user-settings'] });
    },
  });

  const resetUserSettingsMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', '/api/user-settings/reset');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/settings/merged'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user-settings'] });
    },
  });

  const saveAsDefaultsMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', '/api/admin/setting-defaults/save');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/setting-defaults'] });
      queryClient.invalidateQueries({ queryKey: ['/api/settings/merged'] });
    },
  });

  const rentalSetting = settings.find(s => s.key === 'rental_enabled');
  const categoriesSetting = settings.find(s => s.key === 'visible_categories');

  let rentalEnabled = true;
  let visibleCategories: string[] = [];

  if (rentalSetting) {
    const value = rentalSetting.value;
    if (typeof value === 'string') {
      rentalEnabled = value === 'true';
    } else {
      rentalEnabled = Boolean(value);
    }
  }

  if (categoriesSetting) {
    try {
      const parsed = typeof categoriesSetting.value === 'string' 
        ? JSON.parse(categoriesSetting.value) 
        : categoriesSetting.value;
      visibleCategories = Array.isArray(parsed) ? parsed : [];
    } catch {
      visibleCategories = [];
    }
  }

  const getSetting = (key: string): unknown => {
    const setting = settings.find(s => s.key === key);
    return setting?.value;
  };

  const hasOverride = (key: string): boolean => {
    const setting = settings.find(s => s.key === key);
    return setting?.hasOverride ?? false;
  };

  const hasAnyOverrides = settings.some(s => s.hasOverride);
  const hasDefaults = mergedData?.hasDefaults ?? false;
  const isAdmin = user?.roles?.includes('admin') ?? false;

  return {
    rentalEnabled,
    visibleCategories,
    isLoading: isLoadingGlobal || isLoadingMerged,
    settings,
    getSetting,
    hasOverride,
    hasAnyOverrides,
    hasDefaults,
    isAdmin,
    updateUserSetting: updateUserSettingMutation.mutate,
    updateUserSettingAsync: updateUserSettingMutation.mutateAsync,
    isUpdatingSetting: updateUserSettingMutation.isPending,
    resetToDefaults: resetUserSettingsMutation.mutateAsync,
    isResetting: resetUserSettingsMutation.isPending,
    saveAsDefaults: saveAsDefaultsMutation.mutateAsync,
    isSavingDefaults: saveAsDefaultsMutation.isPending,
  };
}
